import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  SimpleChanges,
  OnChanges,
  Pipe,
  PipeTransform,
  ViewChild,
  ElementRef,
  AfterViewInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { DashboardService, MenuItem } from './../../../pages/dashboard/dashboard.service';
import { QuickMenuItem, QuickMenuService } from '../../side-nav/quick-menu/quick-menu.service';
import { takeUntil } from 'rxjs/operators';
import { Subject, combineLatest } from 'rxjs';
import { LayoutService } from '../../../services/layout.service';
import { DialogService } from '../../../services/dialog.service';
import { UserService } from '../../../pages/staff/users/user.service';
import { UserData } from '../../../services/auth/auth.service';
import { SharedService } from '../../../services/shared/shared.service';
import { Location } from '../../../services/library/library.service';

@Pipe({
  name: 'quickLinkLabel',
  standalone: false,
})
export class QuickLinkLabel implements PipeTransform {
  transform(q: QuickMenuItem) {
    return q.parentLabels.join(' > ') + ` > ${q.label}`;
  }
}

@Component({
  selector: 'ag-staff-navigation',
  templateUrl: './staff-navigation.component.html',
  styleUrls: ['./staff-navigation.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class StaffNavigationComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @ViewChild('staffHeader') staffHeader: ElementRef<HTMLElement>;
  @Input() staffMenu: MenuItem;
  /** If not staffMenu is supplied, a pageLabel maybe used as an alterative display */
  @Input() pageLabel: string;

  private _sharedService = inject(SharedService);
  private _dialogService = inject(DialogService);
  private _dashboardService = inject(DashboardService);
  private _layoutService = inject(LayoutService);
  quickMenuService = inject(QuickMenuService);
  _userService = inject(UserService);

  private _destroyed$ = new Subject<void>();
  private _staffHeaderObserver: ResizeObserver;

  staffHistory: any;
  homeLocation: Location;
  locations: Location[];
  prevSelectedLocation: Location;
  selectedLocation: Location;
  fullLabel: string;

  userData: UserData;

  ngOnInit(): void {
    window.addEventListener('message', this._onIframeClose);

    this._dashboardService.staffHistory$
      .pipe(takeUntil(this._destroyed$))
      .subscribe((staffHistory) => {
        this.staffHistory = staffHistory;
      });

    this._dashboardService.toggleStaffNavigationDisplay(true);

    this._sharedService.userData$
      .pipe(takeUntil(this._destroyed$))
      .subscribe((userData: UserData) => {
        if (!userData) {
          return;
        }
        this.userData = userData;
        this.handleUserLocationsDropdown(userData);
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.staffMenu?.currentValue) {
      if (this.staffMenu.fullLabel) {
        this.fullLabel = this.staffMenu.fullLabel;
      } else {
        this.fullLabel = this._dashboardService.fullLabel(this.staffMenu.menuItem);
      }

      if (this.userData) {
        this.handleUserLocationsDropdown(this.userData);
      }
    }
  }

  ngAfterViewInit() {
    this._layoutService.setStaffNavigationHeight(this.staffHeader.nativeElement.offsetHeight);

    this._staffHeaderObserver = new ResizeObserver((entries) => {
      const height = entries[0].contentRect.height;
      this._layoutService.setStaffNavigationHeight(height);
    });
    this._staffHeaderObserver.observe(this.staffHeader.nativeElement);
  }

  ngOnDestroy() {
    this._destroyed$.next();
    this._destroyed$.complete();
    window.removeEventListener('message', this._onIframeClose);
    this._dashboardService.toggleStaffNavigationDisplay(false);
    this._layoutService.setStaffNavigationHeight(0);
    this._staffHeaderObserver.disconnect();
  }

  handleUserLocationsDropdown(userData: UserData) {
    this.locations = null;
    this.homeLocation = null;
    this.selectedLocation = null;
    if (this.staffMenu?.menuId === 24 && !userData.isGuest) {
      const locations$ = this._userService.getHomeUserLocations(userData.userId);
      const homeLocation$ = this._userService.getHomeLocation(userData.userId);
      combineLatest([locations$, homeLocation$]).subscribe(([locations, homeLocation]) => {
        if (!locations && !homeLocation) {
          return;
        }
        this.locations = locations;
        this.homeLocation = homeLocation;
        this.selectedLocation = this.locations.find(
          (location) => location.id === this.homeLocation.id,
        );
        this.prevSelectedLocation = this.selectedLocation;
      });
    }
  }

  // called from the ngmodelchange event. need to type the argument
  updateLocation(location: Location) {
    this._dialogService
      .openAlertDialog(
        'Home Location Change',
        `You are about to change your Home Location for this session from ${this.prevSelectedLocation.fullName} to ${location.fullName}`,
        'Ok',
        'Cancel',
      )
      .subscribe((result) => {
        if (result) {
          this._updateHomeLocation();
        } else {
          this.selectedLocation = this.prevSelectedLocation;
        }
      });
  }

  private _updateHomeLocation() {
    this.prevSelectedLocation = this.selectedLocation;
    this._userService
      .setHomeLocation(this.userData.userId, this.selectedLocation.id)
      .subscribe(() => {
        console.log('Home Location Updated: ', this.selectedLocation);
      });
  }

  openStaffMenu() {
    this._layoutService.openSideNav({
      template: 'staffMenu',
      position: 'start',
      data: this.staffMenu,
    });
  }

  openQuickLink(item) {
    this._dashboardService.goToMenuItem(item.menuItem);
  }

  openLastStaffHistory() {
    this._dashboardService.popStaffLinksHistory();
  }

  closeStaffModule() {
    this._dashboardService.closeStaff();
  }

  private _onIframeClose = (event) => {
    const data = event.data;
    if (data.Event == 'CloseIframe' && data.Category == 'Close Iframe') {
      this.closeStaffModule();
    }
    if (data == 'close') {
      this.closeStaffModule();
    }
  };
}
